/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FileText, File, FileType, UploadCloud, X, Download } from 'lucide-react';
import { PrimaryButton, OutlineButton } from '../../components/Buttons';
import axiosInstance from '../../services/api/axios';
import { fetchProjectPhases, fetchProjectPhaseMedia } from '../../services/projects';
import { toast } from 'react-toastify';
import FlatList from '../../components/ProjectScreens/FlatList';
import ConstructionPlan from '../../components/ProjectScreens/ConstructionPlan';

import BuildingStatus from '../../components/ProjectScreens/BuildingStatus';
import BuildingsAndConfigs from '../../components/ProjectScreens/BuildingsAndConfigs';
import ProgressStatus from '../../components/ProjectScreens/ProgressStatus';
import DeveloperProjectBoqTable from '../../components/DeveloperProjectBoqTable';
import { useUser } from '../../context/UserContext';
import usePermission from '../../hooks/usePermission';
import { getMediaUrl } from '../../utils/media';
import { snakeToSentence } from '../../utils/helpers';
// F17 (v2): Construction WBS 4th sub-tab

/** Partition /projects/phase/media items into images, guidelines, and boq (shared with initial load and refresh). */
function organizePhaseMediaItems(mediaArray) {
  const organizedMedia = {
    images: [],
    guidelines: [],
    boq: [],
  };
  const items = Array.isArray(mediaArray) ? mediaArray : [];
  items.forEach((item) => {
    const category = item.category || item.mediaType || item.type || item.media_type;
    const contentType = item.contentType || item.content_type || '';
    const fileKey = item.key || '';
    const fileName = item.name || fileKey.split('/').pop() || '';

    if (contentType.startsWith('image/') || category === 'images' || category === 'image' || item.isImage) {
      organizedMedia.images.push(item);
    } else if (
      category === 'guidelines' ||
      category === 'guideline' ||
      category === 'interior_design_guidelines'
    ) {
      organizedMedia.guidelines.push(item);
    } else if (
      category === 'boq' ||
      category === 'rera_boq_document' ||
      category === 'rera_boq'
    ) {
      organizedMedia.boq.push(item);
    } else {
      const lowerFileName = fileName.toLowerCase();
      const lowerFileKey = fileKey.toLowerCase();

      if (
        lowerFileName.includes('guideline') ||
        lowerFileName.includes('design') ||
        lowerFileName.includes('interior') ||
        lowerFileKey.includes('guideline') ||
        lowerFileKey.includes('interior')
      ) {
        organizedMedia.guidelines.push(item);
      } else if (
        lowerFileName.includes('boq') ||
        lowerFileName.includes('rera') ||
        lowerFileKey.includes('boq') ||
        lowerFileKey.includes('rera')
      ) {
        organizedMedia.boq.push(item);
      } else if (contentType && (contentType.includes('pdf') || contentType.includes('doc') || contentType.includes('word'))) {
        organizedMedia.guidelines.push(item);
      }
    }
  });
  return organizedMedia;
}

const PhaseDetails = () => {
  const { projectId, phaseId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('phaseInfo');
  const { userInfo } = useUser();
  const { isChannelPartner } = usePermission();
  const role = userInfo?.persona || "Guest";
  const [activeStatusTab, setActiveStatusTab] = useState(() => {
    return isChannelPartner() ? "project" : "ConstructionPlan";
  });
  const [phase, setPhase] = useState(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [projectStatus, setProjectStatus] = useState([]);

  // Progress images state variables
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [avgWbsPercent, setAvgWbsPercent] = useState(0);
  const [buildingMetadata, setBuildingMetadata] = useState([]);
  const [hasPrivilege] = useState(() => () => true); // Default to true for now
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [phaseMedia, setPhaseMedia] = useState({ images: [], guidelines: [], boq: [] });
  const [pendingGuidelinesFile, setPendingGuidelinesFile] = useState(null);
  const [guidelinesUploading, setGuidelinesUploading] = useState(false);
  const [guidelinesFileInputKey, setGuidelinesFileInputKey] = useState(0);
  const guidelinesFileInputRef = useRef(null);




  // Fetch building metadata for project overview cards and details
  const fetchBuildingMetadata = async () => {
    if (!projectId) return;
    try {
      const response = await axiosInstance.get('/building/metadata', {
        params: { projectPhaseId: phaseId },
      });
      // Handle paginated response format: { items: [...], total: 1, page: 1, ... }
      if (response?.items && Array.isArray(response.items)) {
        setBuildingMetadata(response.items);
      } else if (Array.isArray(response)) {
        setBuildingMetadata(response);
      } else {
        setBuildingMetadata([]);
      }
    } catch (error) {
      console.error('Error fetching building metadata:', error);
      setBuildingMetadata([]);
    }
  };

  useEffect(() => {
    fetchBuildingMetadata();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    const fetchPhaseDetails = async () => {
      try {
        setLoading(true);

        // Fetch phase details
        let phaseData;
        const phaseResponse = await fetchProjectPhases({ projectPhaseId: phaseId });

        // Handle paginated response format: { items: [...], total: 1, page: 1, ... }
        let phasesArray = [];
        if (phaseResponse?.items && Array.isArray(phaseResponse.items)) {
          phasesArray = phaseResponse.items;
        } else if (Array.isArray(phaseResponse)) {
          phasesArray = phaseResponse;
        }

        phaseData = phasesArray.find(p => String(p.projectPhaseId) === String(phaseId) || String(p.id) === String(phaseId));

        // If not found, try fetching all phases for the project
        if (!phaseData) {
          const allPhasesResponse = await fetchProjectPhases({ projectId });
          let allPhasesArray = [];
          if (allPhasesResponse?.items && Array.isArray(allPhasesResponse.items)) {
            allPhasesArray = allPhasesResponse.items;
          } else if (Array.isArray(allPhasesResponse)) {
            allPhasesArray = allPhasesResponse;
          }
          phaseData = allPhasesArray.find(p => String(p.projectPhaseId) === String(phaseId) || String(p.id) === String(phaseId));
        }

        if (!phaseData) {
          toast.error('Phase not found');
          return;
        }

        // Fetch project details
        const projectResponse = await axiosInstance.get(`/projects`, {
          params: {
            projectId,
            customerId: null,
          },
        });

        // Handle paginated response format: { items: [...], total: 1, page: 1, ... }
        let projectData = null;
        if (projectResponse?.items && Array.isArray(projectResponse.items) && projectResponse.items.length > 0) {
          projectData = projectResponse.items[0];
        } else if (Array.isArray(projectResponse) && projectResponse.length > 0) {
          projectData = projectResponse[0];
        }

        setPhase(phaseData);
        setProject(projectData);

        // Fetch phase images after setting phase data
        if (phaseData) {
          try {
            // Fetch all phase media (images, guidelines, boq)
            const mediaData = await fetchProjectPhaseMedia(phaseId, {
              images: true,
              progress: false,
              guidelines: true,
              boq: true,
            });

            const mediaArray = Array.isArray(mediaData) ? mediaData : (mediaData?.items || []);
            const organizedMedia = organizePhaseMediaItems(mediaArray);

            // Set images in phase state
            if (organizedMedia.images.length > 0) {
              setPhase(prevPhase => ({
                ...prevPhase,
                images: organizedMedia.images
              }));
            }

            // Set other media types
            setPhaseMedia(organizedMedia);

          } catch (error) {
            console.error('Error fetching phase media:', error);
          }
        }

      } catch (error) {
        toast.error('Failed to load phase details');
      } finally {
        setLoading(false);
      }
    };

    if (projectId && phaseId) {
      fetchPhaseDetails();
    }
  }, [projectId, phaseId]);



  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles(files);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files);
    setSelectedFiles(files);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const removeImage = (index) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);
  };

  const handleImageRemove = (imageId) => {
    // Handle image removal logic here
    console.log('Removing image:', imageId);
  };

  const handleSubmit = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Please select at least one image');
      return;
    }

    setIsSubmitting(true);
    try {
      // Prepare the form data for updating the phase with images
      const formData = new FormData();
      
      // Append required fields directly to form data (as the API expects)
      formData.append('project_phase_id', phaseId);
      formData.append('project_id', projectId);
      formData.append('name', phase.name || '');
      formData.append('towers', parseInt(phase.towers) || 0);
      formData.append('units', parseInt(phase.units) || 0);

      // Append optional fields if they exist
      if (phase.reraNo) {
        formData.append('rera_no', phase.reraNo);
      }
      if (phase.reraStartDate) {
        formData.append('rera_start_date', phase.reraStartDate);
      }
      if (phase.reraEndDate) {
        formData.append('rera_end_date', phase.reraEndDate);
      }
      if (phase.phaseStatus) {
        formData.append('phase_status', parseInt(phase.phaseStatus) || 1);
      }

      // Append actual files to form data
      selectedFiles.forEach((file) => {
        formData.append('images', file);
      });

      const response = await axiosInstance.put('/projects/phase', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });


      toast.success('Phase images uploaded successfully');

      // Reload media with same params as initial load (projectPhaseId — not project_phase_id)
      await refreshPhaseMedia();

      setIsModalOpen(false);
      setSelectedFiles([]);
      
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Upload failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsSubmitting(false);
    }
  };

  const refreshPhaseMedia = async () => {
    if (!phaseId) return;
    try {
      const mediaData = await fetchProjectPhaseMedia(phaseId, {
        images: true,
        progress: false,
        guidelines: true,
        boq: true,
      });
      const mediaArray = Array.isArray(mediaData) ? mediaData : (mediaData?.items || []);
      const organizedMedia = organizePhaseMediaItems(mediaArray);
      setPhase((prev) =>
        prev ? { ...prev, images: organizedMedia.images } : prev
      );
      setPhaseMedia(organizedMedia);
      setCurrentImageIndex(0);
    } catch (error) {
      console.error('Error refreshing phase media:', error);
    }
  };

  const appendCommonPhaseUpdateFields = (formData) => {
    formData.append('project_phase_id', phaseId);
    formData.append('project_id', projectId);
    formData.append('name', phase.name || '');
    formData.append('towers', parseInt(phase.towers) || 0);
    formData.append('units', parseInt(phase.units) || 0);
    if (phase.reraNo) {
      formData.append('rera_no', phase.reraNo);
    }
    if (phase.reraStartDate) {
      formData.append('rera_start_date', phase.reraStartDate);
    }
    if (phase.reraEndDate) {
      formData.append('rera_end_date', phase.reraEndDate);
    }
    if (phase.phaseStatus) {
      formData.append('phase_status', parseInt(phase.phaseStatus) || 1);
    }
  };

  const handleGuidelinesFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      setPendingGuidelinesFile(file);
    }
  };

  const removePendingGuidelinesFile = () => {
    setPendingGuidelinesFile(null);
    if (guidelinesFileInputRef.current) {
      guidelinesFileInputRef.current.value = '';
    }
  };

  const handleUploadInteriorDesignGuidelines = async () => {
    if (!pendingGuidelinesFile || !phase) {
      toast.error('Please choose a file (PDF, DOC, or DOCX)');
      return;
    }
    setGuidelinesUploading(true);
    try {
      const formData = new FormData();
      appendCommonPhaseUpdateFields(formData);
      formData.append('interior_design_guidelines', pendingGuidelinesFile);
      await axiosInstance.put('/projects/phase', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      toast.success('Interior design guidelines uploaded successfully');
      setPendingGuidelinesFile(null);
      setGuidelinesFileInputKey((k) => k + 1);
      await refreshPhaseMedia();
    } catch (error) {
      console.error('Guidelines upload failed:', error);
      toast.error('Upload failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGuidelinesUploading(false);
    }
  };

  const handleBackToProject = () => {
    navigate(`/project-details/${projectId}#projectdetails`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!phase) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">Phase Not Found</h2>
          <PrimaryButton onClick={handleBackToProject}>
            Back to Project
          </PrimaryButton>
        </div>
      </div>
    );
  }

  const renderPropertiesTab = () => (
    <div style={{ overflowY: 'auto', scrollbarWidth: 'thin', height: '100%' }}>
      <FlatList project={project} projectId={projectId} phaseId={phaseId} />
    </div>
  );

  const renderPhaseInfoTab = () => (
    <div className="p-4 flex flex-col gap-4">

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left Column - Phase Details (50% of the area) */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
          {/* Phase Images — full-bleed hero with prev/next chevrons +
              clickable thumbnail strip + a counter chip. */}
          <div className="mb-5">
            <div className="flex justify-between items-center mb-3">
              <h5 className="font-medium text-slate-700">Phase Images</h5>
              {phase.images && phase.images.length > 0 && (
                <span className="text-xs text-slate-500 font-medium">
                  {currentImageIndex + 1} / {phase.images.length}
                </span>
              )}
            </div>

            {phase.images && phase.images.length > 0 ? (
              <div>
                <div className="relative rounded-xl overflow-hidden bg-slate-100 group">
                  <img
                    src={getMediaUrl(phase.images[currentImageIndex])}
                    alt={`Phase ${currentImageIndex + 1}`}
                    className="w-full h-72 object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                  />
                  {/* Slate fade so the chevrons sit on legible backing */}
                  <div className="absolute inset-x-0 top-0 h-12 bg-gradient-to-b from-black/30 to-transparent pointer-events-none" />
                  <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black/30 to-transparent pointer-events-none" />

                  {phase.images.length > 1 && (
                    <>
                      <button
                        type="button"
                        onClick={() => setCurrentImageIndex((p) =>
                          p > 0 ? p - 1 : phase.images.length - 1)}
                        className="absolute top-1/2 left-3 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-slate-700 hover:text-slate-900 shadow flex items-center justify-center transition-colors"
                        aria-label="Previous image"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={() => setCurrentImageIndex((p) =>
                          p < phase.images.length - 1 ? p + 1 : 0)}
                        className="absolute top-1/2 right-3 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-slate-700 hover:text-slate-900 shadow flex items-center justify-center transition-colors"
                        aria-label="Next image"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </>
                  )}
                </div>
                {/* Thumbnail strip — only renders for ≥2 images. Active
                    thumb gets a flamingo ring. */}
                {phase.images.length > 1 && (
                  <div className="flex gap-2 mt-3 overflow-x-auto pb-1" style={{ scrollbarWidth: "thin" }}>
                    {phase.images.map((img, idx) => (
                      <button
                        type="button"
                        key={idx}
                        onClick={() => setCurrentImageIndex(idx)}
                        className={`relative shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                          idx === currentImageIndex
                            ? "border-flamingo-500 ring-2 ring-flamingo-200"
                            : "border-transparent hover:border-slate-300 opacity-70 hover:opacity-100"
                        }`}
                        aria-label={`Go to image ${idx + 1}`}
                      >
                        <img src={getMediaUrl(img)} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-10 border-2 border-dashed border-slate-300 rounded-xl bg-slate-50/50">
                <div className="w-12 h-12 mx-auto rounded-xl bg-flamingo-50 flex items-center justify-center mb-3">
                  <svg className="w-6 h-6 text-flamingo-500" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M2.25 19.5h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5z" />
                  </svg>
                </div>
                <p className="text-sm text-slate-500 mb-3">No images uploaded yet</p>
                {hasPrivilege("canEdit") && (
                  <PrimaryButton onClick={() => setIsModalOpen(true)}>
                    Upload Images
                  </PrimaryButton>
                )}
              </div>
            )}
          </div>

          {/* Phase Details — key facts laid out as a tidy two-column grid
              with a soft slate label and a strong value, instead of the
              former dense key/value list. */}
          <div className="border-t pt-4">
            <h4 className="font-medium text-slate-700 mb-3">Phase Details</h4>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
              <div>
                <dt className="text-[11px] uppercase tracking-wide text-slate-500">Phase Name</dt>
                <dd className="font-semibold text-slate-900 mt-0.5">{phase.name || "—"}</dd>
              </div>
              <div>
                <dt className="text-[11px] uppercase tracking-wide text-slate-500">RERA Number</dt>
                <dd className="font-semibold text-slate-900 mt-0.5">{phase.reraNo || "—"}</dd>
              </div>
              <div>
                <dt className="text-[11px] uppercase tracking-wide text-slate-500">Towers</dt>
                <dd className="font-semibold text-slate-900 mt-0.5">{phase.towers || 0}</dd>
              </div>
              <div>
                <dt className="text-[11px] uppercase tracking-wide text-slate-500">Units</dt>
                <dd className="font-semibold text-slate-900 mt-0.5">{phase.units || 0}</dd>
              </div>
              <div>
                <dt className="text-[11px] uppercase tracking-wide text-slate-500">RERA Start</dt>
                <dd className="font-semibold text-slate-900 mt-0.5">{phase.reraStartDate || "—"}</dd>
              </div>
              <div>
                <dt className="text-[11px] uppercase tracking-wide text-slate-500">RERA End</dt>
                <dd className="font-semibold text-slate-900 mt-0.5">{phase.reraEndDate || "—"}</dd>
              </div>
            </dl>
          </div>

          {/* Documents Section (RERA BOQ only; interior design guidelines are under Interior Designers tab) */}
          {phaseMedia.boq.length > 0 && (
            <div className="border-t pt-4 mt-4">
              <h4 className="font-medium text-gray-700 mb-3">Documents</h4>
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-3">RERA BOQ Documents</p>
                  <div className="space-y-2">
                    {phaseMedia.boq.map((doc, docIndex) => {
                        const docUrl = getMediaUrl(doc);
                        const docName = doc.name || doc.key?.split('/').pop() || `RERA BOQ ${docIndex + 1}`;
                        const contentType = doc.contentType || '';
                        const isPDF = contentType.includes('pdf');
                        const isDOC = contentType.includes('word') || contentType.includes('document') || contentType.includes('msword');
                        
                        // Get file icon based on type
                        const FileIcon = isPDF ? FileText : isDOC ? FileType : File;
                        const iconColor = isPDF ? 'text-red-500' : isDOC ? 'text-blue-500' : 'text-gray-500';
                        
                        return (
                          <a
                            key={docIndex}
                            href={docUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 p-3 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors group"
                          >
                            <div className={`flex-shrink-0 ${iconColor}`}>
                              <FileIcon size={24} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-800 truncate group-hover:text-green-600">
                                {docName}
                              </p>
                              <p className="text-xs text-gray-500 mt-0.5">
                                {isPDF ? 'PDF Document' : isDOC ? 'Word Document' : 'Document'}
                              </p>
                            </div>
                            <div className="flex-shrink-0">
                              <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                              </svg>
                            </div>
                          </a>
                        );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Column — Buildings & Configs. Same card shell as the
            left (phase images + details) so the two columns balance. */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
          <BuildingsAndConfigs
            projectId={projectId}
            projectPhases={[phase]}
            phaseId={phaseId}
          />
        </div>
      </div>
    </div>
  );

  const renderInteriorDesignersTab = () => (
    <div
      className="p-4 grid grid-cols-1 lg:grid-cols-4 gap-4 lg:items-start lg:min-h-[min(70vh,720px)]"
      style={{ overflowY: 'auto', scrollbarWidth: 'thin' }}
    >
      {/* Main: BOQ 3 columns (~75%) */}
      <div className="min-w-0 lg:col-span-3">
        <h3 className="text-base font-semibold text-slate-900 mb-3">Developer Project BOQ</h3>
        <DeveloperProjectBoqTable
          projectPhaseId={phaseId}
          title=""
          showTitle={false}
        />
      </div>

      {/* Right card: 1 column (~25% of row) */}
      <aside className="lg:col-span-1 lg:sticky lg:top-4 self-start border border-slate-200 rounded-2xl p-5 bg-white shadow-sm">
        <label className="text-sm font-medium text-gray-700 mb-2 block">
          Interior Design Guidelines
        </label>
        <p className="text-xs text-gray-500 mb-3">
          Upload design guidelines and specifications for interior design work in this phase.
        </p>
        <input
          ref={guidelinesFileInputRef}
          key={guidelinesFileInputKey}
          type="file"
          accept=".pdf,.doc,.docx"
          onChange={handleGuidelinesFileSelect}
          className="hidden"
        />
        <OutlineButton
          type="button"
          left
          icon={<UploadCloud className="w-4 h-4" />}
          className="w-full max-w-none justify-center"
          onClick={() => guidelinesFileInputRef.current?.click()}
          aria-label="Choose guidelines file (PDF, DOC, or DOCX)"
        >
          Choose file
        </OutlineButton>
        <p className="text-[11px] text-gray-500 mt-2 text-center">
          PDF, DOC, or DOCX
        </p>
        {pendingGuidelinesFile && (
          <div className="mt-3 flex items-center space-x-2 bg-green-50 border border-green-200 p-2 rounded">
            <FileText className="w-4 h-4 text-green-600 flex-shrink-0" />
            <span className="text-xs text-green-700 flex-1 truncate">
              {snakeToSentence(pendingGuidelinesFile.name)}
            </span>
            <button
              type="button"
              onClick={removePendingGuidelinesFile}
              className="text-red-500 hover:text-red-700 ml-2 flex-shrink-0"
              aria-label="Remove file"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        <div className="mt-3">
          <PrimaryButton
            type="button"
            className="w-full justify-center"
            onClick={handleUploadInteriorDesignGuidelines}
            disabled={guidelinesUploading || !pendingGuidelinesFile}
          >
            {guidelinesUploading ? 'Uploading...' : 'Upload guidelines'}
          </PrimaryButton>
        </div>
        {phaseMedia.guidelines.length > 0 && (
          <div className="mt-4 border-t border-gray-200 pt-4">
            <p className="text-xs font-medium text-gray-600 mb-2">Uploaded guidelines</p>
            <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-0.5">
              {phaseMedia.guidelines.map((doc, docIndex) => {
                const docUrl = getMediaUrl(doc);
                const docName = doc.name || doc.key?.split('/').pop() || `Guidelines ${docIndex + 1}`;
                const contentType = doc.contentType || doc.content_type || '';
                const isPDF = contentType.includes('pdf');
                const isDOC = contentType.includes('word') || contentType.includes('document') || contentType.includes('msword');
                const FileIcon = isPDF ? FileText : isDOC ? FileType : File;
                const iconColor = isPDF ? 'text-red-500' : isDOC ? 'text-blue-500' : 'text-gray-500';
                return (
                  <a
                    key={docIndex}
                    href={docUrl || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-start gap-2 p-2.5 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors group ${!docUrl ? 'pointer-events-none opacity-60' : ''}`}
                  >
                    <div className={`flex-shrink-0 ${iconColor} mt-0.5`}>
                      <FileIcon size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-800 break-words group-hover:text-blue-600">
                        {docName}
                      </p>
                      <p className="text-[10px] text-gray-500 mt-0.5">
                        {isPDF ? 'PDF' : isDOC ? 'Word' : 'Document'}
                      </p>
                    </div>
                    {docUrl && (
                      <Download className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" aria-hidden />
                    )}
                  </a>
                );
              })}
            </div>
          </div>
        )}
      </aside>
    </div>
  );

  const renderStatusTab = () => (
    <div className="p-4">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Status Overview</h3>

      <div className="flex gap-4 ">
        {/* Vertical Status Sub-tabs — 3 items (WBS Status absorbs the former Construction WBS view) */}
        <div className="w-64 h-[420px] flex-shrink-0 bg-white p-4 rounded-lg border">
          <div className="flex flex-col space-y-1 p-1 rounded-lg">
            {role !== "AGENT" && (
              <button
                onClick={() => setActiveStatusTab("ConstructionPlan")}
                className={`flex items-center space-x-3 px-4 py-3 text-sm font-medium rounded-md transition-all duration-200 text-left ${activeStatusTab === "ConstructionPlan"
                  ? "bg-white text-slate-600 shadow-sm border-l-4 border-slate-600"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-200"
                  }`}
              >
                <span>Construction Plan</span>
              </button>
            )}
            <button
              onClick={() => setActiveStatusTab("project")}
              className={`flex items-center space-x-3 px-4 py-3 text-sm font-medium rounded-md transition-all duration-200 text-left ${activeStatusTab === "project"
                ? "bg-white text-slate-600 shadow-sm border-l-4 border-slate-600"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-200"
                }`}
            >

              <span>Project Overview</span>
            </button>
            <button
              onClick={() => setActiveStatusTab("building")}
              className={`flex items-center space-x-3 px-4 py-3 text-sm font-medium rounded-md transition-all duration-200 text-left ${activeStatusTab === "building"
                ? "bg-white text-slate-600 shadow-sm border-l-4 border-slate-600"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-200"
                }`}
            >

              <span>WBS Status</span>
            </button>
          </div>
        </div>

        {/* Status Tab Content — no top padding so it top-aligns with the sub-tab sidebar */}
        <div className="flex-1">
          <div className="px-4 pb-4">
            {role !== "AGENT" && activeStatusTab === "ConstructionPlan" && (
              <div className="space-y-4">
                <ConstructionPlan projectId={projectId} projectStatus={projectStatus} phaseId={phaseId} />
              </div>
            )}

            {activeStatusTab === "project" && (
              <div className="space-y-4">
                <ProgressStatus projectId={projectId} phaseId={phaseId} />
              </div>
            )}

            {activeStatusTab === "building" && (
              <div className="h-full">
                <BuildingStatus
                  projectId={projectId}
                  phaseId={phaseId}
                  projectStatus={projectStatus}
                  projectWiseBuildingMetadata={buildingMetadata}
                  refreshBuildingMetadata={fetchBuildingMetadata}
                  refreshProgressImages={() => { }} // Placeholder function
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );



  const renderTabContent = () => {
    switch (activeTab) {
      case 'properties':
        // Only check hideCpInventory if role is AGENT
        if (role === "AGENT" ? !project?.configuration?.hideCpInventory : true) {
          return renderPropertiesTab();
        }
        // If hideCpInventory is false, fall back to phaseInfo
        return renderPhaseInfoTab();
      case 'phaseInfo':
        return renderPhaseInfoTab();
      case 'status':
        return renderStatusTab();
      case 'interiorDesigners':
        return renderInteriorDesignersTab();
      default:
        return renderPhaseInfoTab();
    }
  };
  console.log("project", project);

  // Build the visible tab list once so the pill bar map can stay clean.
  const phaseTabs = [
    { id: "phaseInfo", label: "Phase Info", show: true },
    { id: "properties", label: "Properties", show: role === "AGENT" ? !project?.configuration?.hideCpInventory : true },
    { id: "status", label: "Status", show: true },
    { id: "interiorDesigners", label: "Interior Designers", show: role === "ADMIN" || role === "DEVELOPER" },
  ].filter((t) => t.show);

  return (
    <div className="min-h-screen p-4 lg:p-6 space-y-4">
      {/* Back button — browser history so it lands wherever the user came
          from (project tab, phase list, etc.) */}
      <button
        type="button"
        onClick={handleBackToProject}
        className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 font-medium"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
        Back to Project
      </button>

      {/* Phase hero — slate gradient matching ProjectDetails / ProjectList,
          with the phase name and headline numbers as chips. */}
      <div className="rounded-2xl shadow-sm bg-gradient-to-r from-slate-800 to-slate-600 px-5 lg:px-6 py-5">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-11 h-11 rounded-xl bg-flamingo-500/20 border border-flamingo-500/30 flex items-center justify-center shrink-0">
              <span className="text-flamingo-300 font-semibold text-base">P</span>
            </div>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-semibold text-white tracking-tight truncate">
                {phase.name || "Phase"}
              </h1>
              <p className="text-xs text-slate-300 mt-0.5">
                {phase.reraNo ? `RERA ${phase.reraNo}` : "Phase details, properties, status & designers"}
              </p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {phase.towers ? (
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-semibold bg-white/10 text-white border border-white/10">
                {phase.towers} {Number(phase.towers) === 1 ? "tower" : "towers"}
              </div>
            ) : null}
            {phase.units ? (
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-semibold bg-flamingo-500 text-white">
                {phase.units} {Number(phase.units) === 1 ? "unit" : "units"}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Tab nav — same pill-underline style as ProjectDetails so the two
          tiers of navigation feel like one product surface. */}
      <div
        className="flex items-center gap-1 overflow-x-auto bg-white border border-slate-200 rounded-2xl px-3"
        style={{ scrollbarWidth: "thin" }}
      >
        {phaseTabs.map((t) => {
          const isActive = activeTab === t.id;
          return (
            <button
              type="button"
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`shrink-0 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                isActive
                  ? "text-flamingo-700 border-flamingo-500"
                  : "text-slate-600 border-transparent hover:text-slate-900 hover:border-slate-300"
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Content Area - Reduced top padding */}
      <div className="flex-1">
        {renderTabContent()}
      </div>

      {/* Upload Modal for Phase Images */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-11/12 max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold">Upload Phase Images</h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-500 hover:text-gray-800"
              >
                ✖
              </button>
            </div>

            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              className="border-2 border-dashed border-gray-300 rounded-lg p-6 flex flex-col items-center justify-center cursor-pointer hover:border-blue-500 transition-colors mb-4"
            >
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileChange}
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="text-blue-600 underline cursor-pointer"
              >
                Click to browse files
              </label>
              <p className="text-gray-500 mt-2">
                or drag and drop images here
              </p>
            </div>

            {selectedFiles.length > 0 && (
              <div>
                <h3 className="text-md font-semibold mb-2">Preview Images:</h3>
                <div className="grid grid-cols-3 gap-4">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="relative">
                      <img
                        src={URL.createObjectURL(file)}
                        alt={file.name}
                        className="w-full h-24 object-cover rounded shadow-lg"
                      />
                      <button
                        onClick={() => removeImage(index)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 text-xs"
                      >
                        ✖
                      </button>
                      <p className="text-sm text-gray-700 mt-1">{file.name}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-4 flex justify-end">
              <PrimaryButton onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? "Uploading..." : "Upload"}
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhaseDetails;